// WHAT: Component for displaying individual chat messages in Ollama conversations
// WHY:  Provides visual distinction between user and assistant messages with avatars
// DOMAIN: LLM → Ollama
// DEPENDENCIES: Card, Avatar, Text components, MessageBubbleProps type
// RISKS:
//   - NOTE: Message content is not sanitized - may contain unsafe HTML
//   - NOTE: Model name display may not be available for all messages
//   - NOTE: Whitespace handling (pre-wrap) may cause layout issues with long messages

import type { ChatMessage, MessageBubbleProperties } from "../types";

import { Bot, User } from "lucide-react";
import { type FC } from "react";

import { Card, Avatar, Text } from "@/components/ui";
import { cn } from "@/shared/utils";

/**
 * WHAT:
 *   Component that displays individual chat messages with proper styling
 *   for user and assistant messages.
 *
 * WHY:
 *   Provides clear visual distinction between user and AI messages with
 *   appropriate styling and avatars.
 *
 * PROPS:
 *   - message: ChatMessage — Message data
 *   - isUser: boolean — Whether message is from user
 *
 * BEHAVIOR:
 *   - Displays avatar (Bot for assistant, User for user)
 *   - Styles message bubble based on sender
 *   - Shows model name if available
 *   - Preserves whitespace in message content
 *
 * RISKS:
 *   - NOTE: Message content is not sanitized - may contain unsafe HTML
 *   - NOTE: Model name display may not be available for all messages
 *   - NOTE: Whitespace handling (pre-wrap) may cause layout issues with long messages
 *
 * @param props
 *
 * @param props.message
 *
 * @param props.isUser
 *
 * @returns JSX element with message bubble
 *
 */
export const MessageBubble: FC<MessageBubbleProperties> = ({ isUser, message }) => {
  const typedMessage: ChatMessage = message;
  const messageContent: string = typedMessage.content;
  const messageModel: string | undefined = typedMessage.model;

  return (
    <div className={cn("flex gap-3 mb-4", isUser ? "justify-end" : "justify-start")}>
      {!isUser && (
        <Avatar className="w-8 h-8 bg-blue-500 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </Avatar>
      )}

      <Card
        className={cn(
          "max-w-[80%] p-4",
          isUser ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-100 border-slate-700",
        )}
      >
        <Text className="whitespace-pre-wrap">{messageContent}</Text>

        {messageModel ? (
          <Text
            className={cn("text-xs mt-2 opacity-70", isUser ? "text-blue-100" : "text-slate-400")}
          >
            Model: {messageModel}
          </Text>
        ) : null}
      </Card>

      {isUser ? (
        <Avatar className="w-8 h-8 bg-green-500 flex items-center justify-center">
          <User className="w-4 h-4 text-white" />
        </Avatar>
      ) : null}
    </div>
  );
};
